import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Instagram, 
  Heart, 
  Users, 
  ExternalLink, 
  Sparkles, 
  Gift,
  Building2,
  Handshake,
  ChevronDown,
  Trophy,
  Loader2,
  Menu,
  X,
  ArrowRight,
  Rocket,
  Lightbulb,
  Target,
  DollarSign,
  Lock,
  RefreshCw,
  Trash2,
  Coffee,
  MessageCircle,
  QrCode,
  Star,
  Calendar,
  CheckCircle,
  Zap,
  CreditCard
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cases de Sucesso Data
const cases = [
  { name: "tomich.com.br", url: "https://tomich.com.br" },
  { name: "tomicloud.com.br", url: "https://tomicloud.com.br" },
  { name: "lucimarbizoni.com.br", url: "https://lucimarbizoni.com.br" },
  { name: "tomidesk.com.br", url: "https://tomidesk.com.br" },
  { name: "midiateam.com.br", url: "https://midiateam.com.br" },
  { name: "ofertiz.com.br", url: "https://ofertiz.com.br" },
  { name: "tomicharte.com.br", url: "https://tomicharte.com.br" },
  { name: "grandmidia.com.br", url: "https://grandmidia.com.br" },
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Início", href: "#hero" },
    { name: "Sorteio", href: "#sorteio" },
    { name: "Cases", href: "#cases" },
    { name: "Hub", href: "#hub" },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-3" : "bg-transparent py-6"
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#hero" className="font-heading font-bold text-xl md:text-2xl tracking-tight">
          <span className="text-white">MESA DE </span>
          <span className="gradient-text">TALENTOS</span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-sm text-[#A1A1AA] hover:text-[#00E5FF] transition-colors duration-300"
              data-testid="nav-link"
            >
              {link.name}
            </a>
          ))}
          <a 
            href="#sorteio" 
            className="btn-primary text-sm px-6 py-3"
            data-testid="header-cta"
          >
            Participar
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass mt-2 mx-4 p-6">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-[#A1A1AA] hover:text-[#00E5FF] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a 
              href="#sorteio" 
              className="btn-primary text-center mt-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Participar
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="hero-bg min-h-screen relative flex items-center"
      data-testid="hero-section"
    >
      <div className="hero-overlay absolute inset-0" />
      <div className="grid-pattern absolute inset-0 opacity-50" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
        <div className="max-w-4xl">
          <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] mb-6 animate-fade-in-up">
            Conexão Global • Evento Exclusivo
          </p>
          
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none mb-8">
            <span className="text-white">Um novo conceito que vai </span>
            <span className="gradient-text">mudar a forma</span>
            <span className="text-white"> de fazer negócios</span>
          </h1>
          
          <p className="text-lg text-[#A1A1AA] max-w-2xl mb-10 leading-relaxed">
            Bem-vindo à Mesa de Talentos. Estamos desenvolvendo algo revolucionário para conectar 
            empresários e transformar a maneira como você faz networking e fecha negócios.
          </p>
          
          <div className="flex flex-col gap-4 w-full max-w-2xl">
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#sorteio" 
                className="btn-primary flex-1 inline-flex items-center justify-center gap-2 text-sm sm:text-base py-4"
                data-testid="hero-cta"
              >
                <Gift size={20} />
                Participar do Sorteio
              </a>
              <a 
                href="#oferta" 
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF0055] to-[#FF00A2] hover:opacity-90 transition-all text-white font-bold rounded-xl px-4 py-4 uppercase tracking-wider text-sm sm:text-base shadow-[0_0_20px_rgba(255,0,85,0.3)] border border-[#FF0055]/50"
              >
                <Zap size={20} className="animate-pulse" />
                Oferta do Evento
              </a>
            </div>
            <a 
              href="#cases" 
              className="btn-secondary w-full inline-flex items-center justify-center gap-2 py-3"
            >
              Ver Cases de Sucesso
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown className="text-[#00E5FF]" size={32} />
      </div>
    </section>
  );
};

// Cases Marquee Section
const CasesMarquee = () => {
  return (
    <section id="cases" className="py-24 bg-[#0A0C10] overflow-hidden" data-testid="cases-section">
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] mb-4">
          Cases de Sucesso
        </p>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">
          O poder da IA em ação
        </h2>
        <p className="text-[#A1A1AA] mt-4 max-w-2xl">
          Conheça alguns dos projetos que já desenvolvemos utilizando inteligência artificial e tecnologia de ponta.
        </p>
      </div>
      
      <div className="relative mb-16">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...cases, ...cases].map((item, index) => (
            <a
              key={index}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mx-8 group"
              data-testid="case-link"
            >
              <span className="font-heading font-bold text-4xl md:text-6xl lg:text-7xl text-stroke group-hover:text-[#00E5FF] transition-all duration-300">
                {item.name}
              </span>
            </a>
          ))}
        </div>
      </div>
      
      {/* All Cases Cards */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cases.map((item, index) => (
            <a 
              key={index}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="card-glass p-6 group text-center"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`case-card-${index}`}
            >
              <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-[#00E5FF]/20 to-[#00FF87]/10 flex items-center justify-center">
                <ExternalLink className="text-[#00E5FF] group-hover:text-[#00FF87] transition-colors" size={20} />
              </div>
              <span className="font-heading font-semibold text-sm text-white group-hover:text-[#00E5FF] transition-colors block">
                {item.name}
              </span>
              <p className="text-xs text-[#A1A1AA] mt-2">
                Clique para visitar
              </p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Giveaway Section
const GiveawaySection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    whatsapp: "",
    company: "",
  });
  const [loading, setLoading] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);

  useEffect(() => {
    fetchParticipantCount();
  }, []);

  const fetchParticipantCount = async () => {
    try {
      const response = await axios.get(`${API}/participants/count`);
      setParticipantCount(response.data.count);
    } catch (error) {
      console.error("Error fetching count:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/participants`, formData);
      toast.success("Cadastro realizado com sucesso!", {
        description: "Você está participando do sorteio!",
      });
      setFormData({ name: "", email: "", whatsapp: "", company: "" });
      fetchParticipantCount();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao realizar cadastro";
      toast.error("Erro", { description: message });
    } finally {
      setLoading(false);
    }
  };

  const rules = [
    { icon: Instagram, text: "Siga @danilotomich no Instagram", highlight: "@danilotomich" },
    { icon: Heart, text: "Curta o post do sorteio", highlight: "post do sorteio" },
    { icon: Users, text: "Marque 3 amigos nos comentários", highlight: "3 amigos" },
  ];

  return (
    <section id="sorteio" className="py-24 md:py-32 relative" data-testid="giveaway-section">
      <div className="grid-pattern absolute inset-0 opacity-30" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] mb-4">
            Exclusivo Conexão Global
          </p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-4">
            Sorteio Especial
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Ganhe uma consultoria completa e criação de site gratuito para sua empresa!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Rules */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass p-8">
              <h3 className="font-heading font-semibold text-xl text-white mb-6">
                Como Participar
              </h3>
              
              <div className="space-y-6">
                {rules.map((rule, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="step-number flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <rule.icon className="text-[#00E5FF] mb-2" size={24} />
                      <p className="text-[#A1A1AA]">{rule.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-white/10">
                <a 
                  href="https://instagram.com/danilotomich" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-secondary w-full inline-flex items-center justify-center gap-2"
                >
                  <Instagram size={20} />
                  Ir para o Instagram
                </a>
              </div>
            </div>

            {/* Counter */}
            <div className="glass p-6 text-center">
              <p className="text-[#A1A1AA] text-sm mb-2">Participantes cadastrados</p>
              <p className="font-heading font-bold text-4xl gradient-text">
                {participantCount}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="glass p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="text-[#00E5FF]" size={24} />
                <h3 className="font-heading font-semibold text-xl text-white">
                  Cadastre-se para o Sorteio
                </h3>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="giveaway-form">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Nome Completo</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Seu nome"
                    required
                    className="form-input w-full"
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="seu@email.com"
                    required
                    className="form-input w-full"
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">WhatsApp</label>
                  <Input
                    type="tel"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    placeholder="(00) 00000-0000"
                    required
                    className="form-input w-full"
                    data-testid="input-whatsapp"
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Empresa</label>
                  <Input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Nome da sua empresa"
                    required
                    className="form-input w-full"
                    data-testid="input-company"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full mt-6"
                  data-testid="submit-registration"
                >
                  {loading ? (
                    <Loader2 className="animate-spin mr-2" size={20} />
                  ) : (
                    <Gift className="mr-2" size={20} />
                  )}
                  Garantir Participação
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Hub Section (Talentos, Negocios & Startups)
const HubSection = () => {
  const [talentos, setTalentos] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [startups, setStartups] = useState([]);
  const [showTalentoForm, setShowTalentoForm] = useState(false);
  const [showNegocioForm, setShowNegocioForm] = useState(false);
  const [showStartupForm, setShowStartupForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [talentoForm, setTalentoForm] = useState({
    name: "",
    description: "",
    industry: "",
    contact_email: "",
    website: "",
  });

  const [negocioForm, setNegocioForm] = useState({
    company_name: "",
    offer: "",
    need: "",
    contact_whatsapp: "",
    contact_email: "",
  });

  const [startupForm, setStartupForm] = useState({
    name: "",
    founder_name: "",
    idea_description: "",
    problem_solved: "",
    target_audience: "",
    investment_needed: "",
    contact_whatsapp: "",
    contact_email: "",
  });

  useEffect(() => {
    fetchTalentos();
    fetchNegocios();
    fetchStartups();
  }, []);

  const fetchTalentos = async () => {
    try {
      const response = await axios.get(`${API}/talentos`);
      setTalentos(response.data);
    } catch (error) {
      console.error("Error fetching talentos:", error);
    }
  };

  const fetchNegocios = async () => {
    try {
      const response = await axios.get(`${API}/negocios`);
      setNegocios(response.data);
    } catch (error) {
      console.error("Error fetching negocios:", error);
    }
  };

  const fetchStartups = async () => {
    try {
      const response = await axios.get(`${API}/startups`);
      setStartups(response.data);
    } catch (error) {
      console.error("Error fetching startups:", error);
    }
  };

  const handleTalentoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/talentos`, talentoForm);
      toast.success("Empresa cadastrada com sucesso!");
      setTalentoForm({ name: "", description: "", industry: "", contact_email: "", website: "" });
      setShowTalentoForm(false);
      fetchTalentos();
    } catch (error) {
      toast.error("Erro ao cadastrar empresa");
    } finally {
      setLoading(false);
    }
  };

  const handleNegocioSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/negocios`, negocioForm);
      toast.success("Oferta de negócio cadastrada!");
      setNegocioForm({ company_name: "", offer: "", need: "", contact_whatsapp: "", contact_email: "" });
      setShowNegocioForm(false);
      fetchNegocios();
    } catch (error) {
      toast.error("Erro ao cadastrar negócio");
    } finally {
      setLoading(false);
    }
  };

  const handleStartupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/startups`, startupForm);
      toast.success("Startup cadastrada com sucesso!");
      setStartupForm({ 
        name: "", founder_name: "", idea_description: "", problem_solved: "", 
        target_audience: "", investment_needed: "", contact_whatsapp: "", contact_email: "" 
      });
      setShowStartupForm(false);
      fetchStartups();
    } catch (error) {
      toast.error("Erro ao cadastrar startup");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      seeking: { label: "Buscando Investimento", color: "bg-[#00E5FF]/20 text-[#00E5FF]" },
      in_talks: { label: "Em Negociação", color: "bg-yellow-500/20 text-yellow-400" },
      funded: { label: "Financiado", color: "bg-[#00FF87]/20 text-[#00FF87]" },
    };
    const config = statusConfig[status] || statusConfig.seeking;
    return <span className={`text-xs px-2 py-1 ${config.color}`}>{config.label}</span>;
  };

  return (
    <section id="hub" className="py-24 md:py-32 bg-[#0A0C10]" data-testid="hub-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase text-[#00E5FF] mb-4">
            Hub de Conexões
          </p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white">
            Conecte-se com outros empresários
          </h2>
        </div>

        <Tabs defaultValue="talentos" className="w-full" data-testid="hub-tabs">
          <TabsList className="w-full max-w-2xl mx-auto mb-10 bg-transparent border border-white/10 p-1 grid grid-cols-3">
            <TabsTrigger 
              value="talentos" 
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black font-semibold text-xs sm:text-sm"
            >
              <Building2 className="mr-1 sm:mr-2" size={16} />
              Talentos
            </TabsTrigger>
            <TabsTrigger 
              value="negocios"
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black font-semibold text-xs sm:text-sm"
            >
              <Handshake className="mr-1 sm:mr-2" size={16} />
              Negócios
            </TabsTrigger>
            <TabsTrigger 
              value="startups"
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black font-semibold text-xs sm:text-sm"
            >
              <Rocket className="mr-1 sm:mr-2" size={16} />
              Start-ups
            </TabsTrigger>
          </TabsList>

          {/* Talentos Tab */}
          <TabsContent value="talentos" className="mt-0">
            <div className="flex justify-end mb-6">
              <Button 
                onClick={() => setShowTalentoForm(!showTalentoForm)}
                className="btn-secondary"
              >
                {showTalentoForm ? "Cancelar" : "+ Adicionar Empresa"}
              </Button>
            </div>

            {showTalentoForm && (
              <div className="glass p-6 mb-8">
                <form onSubmit={handleTalentoSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Nome da Empresa"
                    value={talentoForm.name}
                    onChange={(e) => setTalentoForm({ ...talentoForm, name: e.target.value })}
                    required
                    className="form-input"
                  />
                  <Input
                    placeholder="Segmento"
                    value={talentoForm.industry}
                    onChange={(e) => setTalentoForm({ ...talentoForm, industry: e.target.value })}
                    required
                    className="form-input"
                  />
                  <Input
                    placeholder="Email de Contato"
                    type="email"
                    value={talentoForm.contact_email}
                    onChange={(e) => setTalentoForm({ ...talentoForm, contact_email: e.target.value })}
                    className="form-input"
                  />
                  <Input
                    placeholder="Website"
                    value={talentoForm.website}
                    onChange={(e) => setTalentoForm({ ...talentoForm, website: e.target.value })}
                    className="form-input"
                  />
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Descrição da empresa"
                      value={talentoForm.description}
                      onChange={(e) => setTalentoForm({ ...talentoForm, description: e.target.value })}
                      required
                      className="form-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="submit" disabled={loading} className="btn-primary">
                      {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                      Cadastrar Empresa
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {talentos.length === 0 ? (
                <div className="col-span-full text-center py-12 text-[#A1A1AA]">
                  <Building2 className="mx-auto mb-4 opacity-50" size={48} />
                  <p>Nenhuma empresa cadastrada ainda.</p>
                  <p className="text-sm mt-2">Seja o primeiro a adicionar sua empresa!</p>
                </div>
              ) : (
                talentos.map((talento, index) => (
                  <div 
                    key={talento.id} 
                    className="card-glass p-6"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid="talento-card"
                  >
                    <div className="h-24 bg-gradient-to-br from-[#00E5FF]/20 to-[#00FF87]/10 mb-4 flex items-center justify-center">
                      <Building2 className="text-[#00E5FF]" size={32} />
                    </div>
                    <h4 className="font-heading font-semibold text-white mb-2">{talento.name}</h4>
                    <span className="inline-block text-xs px-2 py-1 bg-[#00E5FF]/10 text-[#00E5FF] mb-3">
                      {talento.industry}
                    </span>
                    <p className="text-sm text-[#A1A1AA] mb-4">{talento.description}</p>
                    {talento.website && (
                      <a 
                        href={talento.website.startsWith('http') ? talento.website : `https://${talento.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00E5FF] text-sm flex items-center gap-1 hover:underline"
                      >
                        Visitar site <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Negocios Tab */}
          <TabsContent value="negocios" className="mt-0">
            <div className="flex justify-end mb-6">
              <Button 
                onClick={() => setShowNegocioForm(!showNegocioForm)}
                className="btn-secondary"
              >
                {showNegocioForm ? "Cancelar" : "+ Oferecer Permuta"}
              </Button>
            </div>

            {showNegocioForm && (
              <div className="glass p-6 mb-8">
                <form onSubmit={handleNegocioSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Nome da Empresa"
                    value={negocioForm.company_name}
                    onChange={(e) => setNegocioForm({ ...negocioForm, company_name: e.target.value })}
                    required
                    className="form-input"
                  />
                  <Input
                    placeholder="WhatsApp"
                    value={negocioForm.contact_whatsapp}
                    onChange={(e) => setNegocioForm({ ...negocioForm, contact_whatsapp: e.target.value })}
                    required
                    className="form-input"
                  />
                  <Input
                    placeholder="O que você oferece?"
                    value={negocioForm.offer}
                    onChange={(e) => setNegocioForm({ ...negocioForm, offer: e.target.value })}
                    required
                    className="form-input"
                  />
                  <Input
                    placeholder="O que você precisa?"
                    value={negocioForm.need}
                    onChange={(e) => setNegocioForm({ ...negocioForm, need: e.target.value })}
                    required
                    className="form-input"
                  />
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Email de Contato (opcional)"
                      type="email"
                      value={negocioForm.contact_email}
                      onChange={(e) => setNegocioForm({ ...negocioForm, contact_email: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="submit" disabled={loading} className="btn-primary">
                      {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                      Publicar Oferta
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {negocios.length === 0 ? (
                <div className="col-span-full text-center py-12 text-[#A1A1AA]">
                  <Handshake className="mx-auto mb-4 opacity-50" size={48} />
                  <p>Nenhuma oferta de permuta ainda.</p>
                  <p className="text-sm mt-2">Seja o primeiro a oferecer uma troca!</p>
                </div>
              ) : (
                negocios.map((negocio, index) => (
                  <div 
                    key={negocio.id} 
                    className="card-glass p-6"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid="negocio-card"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-[#00FF87]/20 flex items-center justify-center">
                        <Handshake className="text-[#00FF87]" size={20} />
                      </div>
                      <h4 className="font-heading font-semibold text-white">{negocio.company_name}</h4>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <div className="p-3 bg-[#00E5FF]/5 border-l-2 border-[#00E5FF]">
                        <p className="text-xs text-[#00E5FF] mb-1">Oferece</p>
                        <p className="text-white text-sm">{negocio.offer}</p>
                      </div>
                      <div className="p-3 bg-[#00FF87]/5 border-l-2 border-[#00FF87]">
                        <p className="text-xs text-[#00FF87] mb-1">Precisa</p>
                        <p className="text-white text-sm">{negocio.need}</p>
                      </div>
                    </div>

                    <a 
                      href={`https://wa.me/${negocio.contact_whatsapp.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary w-full text-center text-sm py-2"
                    >
                      Entrar em Contato
                    </a>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Startups Tab */}
          <TabsContent value="startups" className="mt-0">
            {/* Conceito Explicativo */}
            <div className="glass p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00E5FF] to-[#00FF87] flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="text-black" size={24} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-2">
                    O que é a aba Start-ups?
                  </h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
                    Este é o espaço onde <span className="text-[#00E5FF]">empreendedores com ideias inovadoras</span> podem 
                    apresentar seus projetos para potenciais investidores e patrocinadores. Se você tem uma ideia que pode 
                    transformar o mercado, cadastre aqui e conecte-se com empresários interessados em investir em novos negócios.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-white/5">
                      <Lightbulb className="text-[#00E5FF] mx-auto mb-2" size={20} />
                      <p className="text-xs text-[#A1A1AA]">Apresente sua ideia</p>
                    </div>
                    <div className="p-3 bg-white/5">
                      <Target className="text-[#00FF87] mx-auto mb-2" size={20} />
                      <p className="text-xs text-[#A1A1AA]">Conecte-se com investidores</p>
                    </div>
                    <div className="p-3 bg-white/5">
                      <DollarSign className="text-yellow-400 mx-auto mb-2" size={20} />
                      <p className="text-xs text-[#A1A1AA]">Receba patrocínio</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mb-6">
              <Button 
                onClick={() => setShowStartupForm(!showStartupForm)}
                className="btn-secondary"
                data-testid="add-startup-btn"
              >
                {showStartupForm ? "Cancelar" : "+ Cadastrar Minha Ideia"}
              </Button>
            </div>

            {showStartupForm && (
              <div className="glass p-6 mb-8">
                <h4 className="font-heading font-semibold text-lg text-white mb-4 flex items-center gap-2">
                  <Rocket className="text-[#00E5FF]" size={20} />
                  Cadastre sua Start-up
                </h4>
                <form onSubmit={handleStartupSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Nome da Start-up"
                    value={startupForm.name}
                    onChange={(e) => setStartupForm({ ...startupForm, name: e.target.value })}
                    required
                    className="form-input"
                    data-testid="startup-name"
                  />
                  <Input
                    placeholder="Nome do Fundador"
                    value={startupForm.founder_name}
                    onChange={(e) => setStartupForm({ ...startupForm, founder_name: e.target.value })}
                    required
                    className="form-input"
                    data-testid="startup-founder"
                  />
                  <div className="md:col-span-2">
                    <Textarea
                      placeholder="Descreva sua ideia em detalhes..."
                      value={startupForm.idea_description}
                      onChange={(e) => setStartupForm({ ...startupForm, idea_description: e.target.value })}
                      required
                      className="form-input min-h-[100px]"
                      data-testid="startup-idea"
                    />
                  </div>
                  <Input
                    placeholder="Qual problema sua ideia resolve?"
                    value={startupForm.problem_solved}
                    onChange={(e) => setStartupForm({ ...startupForm, problem_solved: e.target.value })}
                    required
                    className="form-input"
                    data-testid="startup-problem"
                  />
                  <Input
                    placeholder="Público-alvo"
                    value={startupForm.target_audience}
                    onChange={(e) => setStartupForm({ ...startupForm, target_audience: e.target.value })}
                    required
                    className="form-input"
                    data-testid="startup-audience"
                  />
                  <Input
                    placeholder="Investimento necessário (ex: R$ 50.000)"
                    value={startupForm.investment_needed}
                    onChange={(e) => setStartupForm({ ...startupForm, investment_needed: e.target.value })}
                    required
                    className="form-input"
                    data-testid="startup-investment"
                  />
                  <Input
                    placeholder="WhatsApp para contato"
                    value={startupForm.contact_whatsapp}
                    onChange={(e) => setStartupForm({ ...startupForm, contact_whatsapp: e.target.value })}
                    required
                    className="form-input"
                    data-testid="startup-whatsapp"
                  />
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Email (opcional)"
                      type="email"
                      value={startupForm.contact_email}
                      onChange={(e) => setStartupForm({ ...startupForm, contact_email: e.target.value })}
                      className="form-input"
                      data-testid="startup-email"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="submit" disabled={loading} className="btn-primary" data-testid="submit-startup">
                      {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Rocket className="mr-2" size={18} />}
                      Publicar Start-up
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {startups.length === 0 ? (
                <div className="col-span-full text-center py-12 text-[#A1A1AA]">
                  <Rocket className="mx-auto mb-4 opacity-50" size={48} />
                  <p>Nenhuma start-up cadastrada ainda.</p>
                  <p className="text-sm mt-2">Seja o primeiro a apresentar sua ideia!</p>
                </div>
              ) : (
                startups.map((startup, index) => (
                  <div 
                    key={startup.id} 
                    className="card-glass p-6"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid="startup-card"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#00E5FF]/20 to-[#00FF87]/20 flex items-center justify-center">
                          <Rocket className="text-[#00E5FF]" size={24} />
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold text-white">{startup.name}</h4>
                          <p className="text-xs text-[#A1A1AA]">por {startup.founder_name}</p>
                        </div>
                      </div>
                      {getStatusBadge(startup.status)}
                    </div>
                    
                    <p className="text-sm text-[#A1A1AA] mb-4 line-clamp-3">{startup.idea_description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="text-[#00E5FF] flex-shrink-0 mt-0.5" size={14} />
                        <p className="text-xs text-[#A1A1AA]"><span className="text-white">Problema:</span> {startup.problem_solved}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Target className="text-[#00FF87] flex-shrink-0 mt-0.5" size={14} />
                        <p className="text-xs text-[#A1A1AA]"><span className="text-white">Público:</span> {startup.target_audience}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <DollarSign className="text-yellow-400 flex-shrink-0 mt-0.5" size={14} />
                        <p className="text-xs text-[#A1A1AA]"><span className="text-white">Investimento:</span> {startup.investment_needed}</p>
                      </div>
                    </div>

                    <a 
                      href={`https://wa.me/${startup.contact_whatsapp.replace(/\D/g, '')}?text=Olá! Vi sua startup "${startup.name}" na Mesa de Talentos e gostaria de saber mais.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary w-full text-center text-sm py-2"
                    >
                      Tenho Interesse em Investir
                    </a>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

// Public Draw Section (para mostrar no evento)
const PublicDrawSection = () => {
  const [winner, setWinner] = useState(null);
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    fetchParticipants();
    checkCurrentWinner();
    
    // Polling so the screen updates automatically when admin draws!
    const interval = setInterval(() => {
      checkCurrentWinner();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(`${API}/participants`);
      setParticipants(response.data);
    } catch (error) {}
  };

  const checkCurrentWinner = async () => {
    try {
      const response = await axios.get(`${API}/giveaway/winner`);
      if (response.data.winner) {
        setWinner(response.data.winner);
      } else {
        setWinner(null);
      }
    } catch (error) {}
  };

  const availableCount = participants.filter(p => !p.is_winner).length;

  return (
    <section 
      className="py-24 relative"
      style={{
        backgroundImage: `linear-gradient(rgba(5,5,5,0.9), rgba(5,5,5,0.95)), url('https://static.prod-images.emergentagent.com/jobs/2bd265d3-1f89-4bb5-a460-d0b11ad38a90/images/8453628cc768a974d80f0e7420f620d8c2c15c45ef7bd65a92d5cabf66634884.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="public-draw-section"
      id="sorteio-resultado"
    >
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="glass p-8 md:p-12">
          <Trophy className="mx-auto text-[#00E5FF] mb-6 neon-glow" size={64} />
          
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-4">
            Resultado do Sorteio
          </h2>
          <p className="text-[#A1A1AA] mb-8">
            {participants.length} participantes cadastrados • {availableCount} na fila
          </p>

          {/* Winner Display */}
          {winner ? (
             <div className="winner-reveal p-8 bg-gradient-to-br from-[#00E5FF]/20 to-[#00FF87]/10 border border-[#00E5FF]/30 rounded-xl relative overflow-hidden">
               <div className="absolute inset-0 opacity-50 mix-blend-overlay pointer-events-none"></div>
               <div className="relative z-10">
                 <h3 className="text-2xl font-heading font-black text-white mb-2 uppercase tracking-wide">🏆 PARABÉNS! 🏆</h3>
                 <p className="text-[#00E5FF] text-sm mb-6 uppercase tracking-widest font-semibold flex items-center justify-center gap-2">
                   Ganhador do Sorteio
                 </p>
                 
                 <p className="font-heading font-black text-4xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-white mb-4 drop-shadow-sm">
                   {winner.name}
                 </p>
                 
                 <div className="bg-black/40 inline-block px-6 py-3 rounded-full border border-white/10 mb-6">
                   <p className="text-[#00FF87] text-xl font-medium tracking-wide">{winner.company}</p>
                 </div>
                 
                 {winner.drawn_at && (
                   <div className="flex items-center justify-center gap-2 text-[#A1A1AA] text-sm mt-4 pt-4 border-t border-white/10">
                     <span className="opacity-80">Sorteado em: {new Date(winner.drawn_at).toLocaleDateString('pt-BR')} às {new Date(winner.drawn_at).toLocaleTimeString('pt-BR')}</span>
                   </div>
                 )}
               </div>
             </div>
          ) : (
            <div className="glass p-12 text-center py-16 opacity-80 animate-pulse">
              <Gift className="mx-auto text-[#00E5FF] mb-4 opacity-50" size={48} />
              <h3 className="font-heading font-bold text-2xl text-white mb-2">Aguardando Sorteio...</h3>
              <p className="text-[#A1A1AA]">O prêmio em breve terá um ganhador!</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Admin Page Component (página separada /admin)
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [participants, setParticipants] = useState([]);
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchParticipants();
    checkCurrentWinner();
  }, []);

  const fetchParticipants = async () => {
    try {
      const response = await axios.get(`${API}/participants`);
      setParticipants(response.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const checkCurrentWinner = async () => {
    try {
      const response = await axios.get(`${API}/giveaway/winner`);
      if (response.data.winner) {
        setWinner(response.data.winner);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const verifyPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify?password=${encodeURIComponent(password)}`);
      setIsAuthenticated(true);
      toast.success("Acesso liberado!");
    } catch (error) {
      toast.error("Senha incorreta!");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const resetDraw = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-giveaway?password=${encodeURIComponent(password)}`);
      setWinner(null);
      fetchParticipants();
      toast.success("Sorteio resetado! Todos os participantes podem ser sorteados novamente.");
    } catch (error) {
      toast.error("Erro ao resetar sorteio");
    } finally {
      setLoading(false);
    }
  };

  const adminDrawWinner = async () => {
    if (availableCount === 0) {
      toast.error("Não há participantes disponíveis para sorteio");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/draw?password=${encodeURIComponent(password)}`);
      setWinner(response.data);
      fetchParticipants();
      toast.success("Sorteio realizado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar sorteio");
    } finally {
      setLoading(false);
    }
  };

  const deleteParticipant = async (participantId, participantName) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${participantName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/participants/${participantId}?password=${encodeURIComponent(password)}`);
      fetchParticipants();
      toast.success("Participante excluído com sucesso!");
    } catch (error) {
      toast.error("Erro ao excluir participante");
    }
  };

  const availableCount = participants.filter(p => !p.is_winner).length;
  const winnersCount = participants.filter(p => p.is_winner).length;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="glass p-8 md:p-12 max-w-md w-full text-center">
          <Lock className="mx-auto text-[#00E5FF] mb-6" size={48} />
          <h1 className="font-heading font-bold text-2xl text-white mb-2">
            Painel Administrativo
          </h1>
          <p className="text-[#A1A1AA] mb-8">Digite a senha para acessar</p>

          <form onSubmit={verifyPassword} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha de administrador"
              className="form-input text-center"
              data-testid="admin-password-input"
            />
            <Button 
              type="submit"
              disabled={loading || !password}
              className="btn-primary w-full"
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Lock className="mr-2" size={18} />}
              Entrar
            </Button>
          </form>

          <a href="/" className="text-[#00E5FF] text-sm mt-6 inline-block hover:underline">
            ← Voltar para o site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl text-white">Painel Administrativo</h1>
            <p className="text-[#A1A1AA] text-sm">Mesa de Talentos - Gerenciamento do Sorteio</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#00FF87] text-sm flex items-center gap-1">
              <Lock size={14} /> Admin
            </span>
            <a href="/" className="btn-secondary text-sm px-4 py-2">
              Ver Site
            </a>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="glass p-6 text-center">
            <p className="text-3xl font-heading font-bold text-white">{participants.length}</p>
            <p className="text-[#A1A1AA] text-sm">Total Cadastrados</p>
          </div>
          <div className="glass p-6 text-center">
            <p className="text-3xl font-heading font-bold text-[#00E5FF]">{availableCount}</p>
            <p className="text-[#A1A1AA] text-sm">Disponíveis p/ Sorteio</p>
          </div>
          <div className="glass p-6 text-center">
            <p className="text-3xl font-heading font-bold text-[#00FF87]">{winnersCount}</p>
            <p className="text-[#A1A1AA] text-sm">Já Sorteados</p>
          </div>
        </div>

        {/* Current Winner */}
        {winner && (
          <div className="glass p-6 mb-8">
            <h3 className="font-heading font-semibold text-lg text-white mb-4">Último Ganhador</h3>
            <div className="bg-gradient-to-br from-[#00E5FF]/20 to-[#00FF87]/10 p-6 border border-[#00E5FF]/30 rounded-lg">
              <p className="font-heading font-bold text-2xl text-white">{winner.name}</p>
              <p className="text-[#00FF87] text-lg mb-2">{winner.company}</p>
              {winner.drawn_at && (
                <p className="text-white/60 text-sm mb-4">
                  Sorteado em: {new Date(winner.drawn_at).toLocaleString('pt-BR')}
                </p>
              )}
              <p className="text-[#A1A1AA] text-sm">WhatsApp: {winner.whatsapp}</p>
              <p className="text-[#A1A1AA] text-sm">Email: {winner.email}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="glass p-6 mb-8">
          <h3 className="font-heading font-semibold text-lg text-white mb-4">Ações do Sorteio</h3>
          
          <div className="space-y-4">
            {/* Draw Action */}
            <div className="p-5 bg-gradient-to-r from-[#00E5FF]/10 to-transparent border border-[#00E5FF]/20 rounded-lg">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2"><Gift size={18} className="text-[#00E5FF]" /> Realizar Sorteio Público</h4>
              <p className="text-[#A1A1AA] text-sm mb-4">
                Sorteia instantaneamente um participante aleatório. O resultado aparecerá automaticamente na tela inicial (início).
              </p>
              <Button 
                onClick={adminDrawWinner}
                disabled={loading || availableCount === 0}
                className="btn-primary"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Gift className="mr-2" size={18} />}
                Sortear Novo Ganhador
              </Button>
            </div>

            <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
              <h4 className="text-white font-semibold mb-2">Resetar Sorteio</h4>
              <p className="text-[#A1A1AA] text-sm mb-4">
                Remove todos os ganhadores anteriores, permitindo sortear novamente entre todos os participantes.
                Os cadastros são mantidos.
              </p>
              <Button 
                onClick={resetDraw}
                disabled={loading}
                className="btn-secondary"
                data-testid="admin-reset-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <RefreshCw className="mr-2" size={18} />}
                Resetar Sorteio
              </Button>
            </div>
          </div>
        </div>

        {/* Participants List */}
        <div className="glass p-6">
          <h3 className="font-heading font-semibold text-lg text-white mb-4">Participantes Cadastrados</h3>
          
          {participants.length === 0 ? (
            <p className="text-[#A1A1AA] text-center py-8">Nenhum participante cadastrado ainda.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-4 text-[#A1A1AA] text-sm font-medium">Nome</th>
                    <th className="py-3 px-4 text-[#A1A1AA] text-sm font-medium">Empresa</th>
                    <th className="py-3 px-4 text-[#A1A1AA] text-sm font-medium">WhatsApp</th>
                    <th className="py-3 px-4 text-[#A1A1AA] text-sm font-medium">Status</th>
                    <th className="py-3 px-4 text-[#A1A1AA] text-sm font-medium text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((p) => (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{p.name}</td>
                      <td className="py-3 px-4 text-[#A1A1AA]">{p.company}</td>
                      <td className="py-3 px-4 text-[#A1A1AA]">{p.whatsapp}</td>
                      <td className="py-3 px-4">
                        {p.is_winner ? (
                          <span className="text-xs px-2 py-1 bg-[#00FF87]/20 text-[#00FF87]">Ganhador</span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-white/10 text-[#A1A1AA]">Participando</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => deleteParticipant(p.id, p.name)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-2 rounded transition-colors"
                          title="Excluir participante"
                          data-testid={`delete-participant-${p.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-24 md:py-32 bg-[#050505] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter">
            <span className="text-white">MESA DE</span>
            <br />
            <span className="gradient-text">TALENTOS</span>
          </h2>
        </div>

        <div className="flex flex-wrap justify-center gap-6 mb-12">
          {cases.map((item) => (
            <a
              key={item.name}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A1A1AA] hover:text-[#00E5FF] transition-colors text-sm"
            >
              {item.name}
            </a>
          ))}
        </div>

        <div className="text-center text-[#A1A1AA] text-sm flex flex-col items-center gap-4">
          <p>© 2024 Mesa de Talentos. Todos os direitos reservados.</p>
          <a 
            href="https://instagram.com/danilotomich" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#00E5FF] hover:underline flex items-center justify-center gap-2"
          >
            <Instagram size={16} />
            @danilotomich
          </a>
          <p className="mt-4 text-xs opacity-70">
            Desenvolvido com carinho por Tomich Tecnologia
          </p>
        </div>
      </div>
    </footer>
  );
};

// Offer Section
const OfferSection = () => {
  const pixKeyClean = "+5527981761251";
  const pixString = generatePix(pixKeyClean, "Danilo Tomich", "Vitoria", "799.00");
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(pixString)}`;

  const handleCopyPix = () => {
    navigator.clipboard.writeText(pixString);
    toast.success("Código PIX copiado para a área de transferência!");
  };

  return (
    <section id="oferta" className="py-24 relative bg-[#0A0C10] overflow-hidden" data-testid="offer-section">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF0055]/5 to-transparent pointer-events-none" />
      <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
        
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF0055]/10 border border-[#FF0055]/30 text-[#FF0055] font-semibold text-sm tracking-widest uppercase mb-8 animate-pulse">
          <Zap size={16} /> Somente Durante Este Evento
        </div>

        <h2 className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter text-white mb-6 uppercase">
          HOJE NO <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF0055] to-[#FF00A2]">MERCADO DIGITAL</span>
        </h2>

        {/* Comparation Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-16 text-left">
          <div className="glass p-8 border-red-500/20 bg-red-950/10 rounded-2xl flex flex-col justify-between">
            <div>
              <h3 className="font-heading font-bold text-xl text-white/50 mb-6 flex items-center gap-2">
                <span className="text-red-400">✗</span> Como a maioria faz:
              </h3>
              <ul className="space-y-4 text-[#A1A1AA]">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">•</span> 
                  <span>Desenvolvimento de site: <strong className="text-gray-300 line-through decoration-red-500/50">a partir de R$ 3.000</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">•</span> 
                  <span>Hospedagem profissional: <strong className="text-gray-300 line-through decoration-red-500/50">R$ 70/mês</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 mt-1">•</span> 
                  <span>Consultoria e ajustes: <strong className="text-gray-300 line-through decoration-red-500/50">R$ 100/hora</strong></span>
                </li>
              </ul>
            </div>
            <div className="mt-8 pt-6 border-t border-red-500/20">
              <p className="text-sm text-[#A1A1AA] uppercase tracking-widest mb-1">Custo Total Estimado (1 Ano)</p>
              <p className="font-heading font-black text-3xl text-gray-400 line-through decoration-red-500/70">R$ 4.040,00+</p>
            </div>
          </div>

          <div className="glass p-8 border-[#00E5FF]/30 bg-[#00E5FF]/5 rounded-2xl relative flex flex-col justify-between">
            <div>
              <div className="absolute -top-3 right-4 bg-[#00E5FF] text-black text-xs font-bold px-3 py-1 rounded-full uppercase shadow-[0_0_15px_rgba(0,229,255,0.4)]">
                Nosso Método
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-2 flex items-center gap-2">
                <span className="text-[#00E5FF]">✓</span> Modelo Acessível
              </h3>
              <p className="text-sm text-[#00E5FF]/80 mb-6 italic">...futurista, moderno e rápido.</p>
              <ul className="space-y-4 text-white">
                <li className="flex items-start gap-3"><CheckCircle className="text-[#00E5FF] mt-1 shrink-0" size={18} /> <span className="font-semibold shadow-sm">Desenvolvimento completo por apenas 1 salário mínimo</span></li>
                <li className="flex items-start gap-3"><CheckCircle className="text-[#00E5FF] mt-1 shrink-0" size={18} /> <span className="font-semibold shadow-sm">Hospedagem profissional por apenas R$ 49,90/mês</span></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Transition */}
        <div className="mb-16">
          <p className="text-xl md:text-2xl text-[#A1A1AA] max-w-2xl mx-auto leading-relaxed font-light">
            Só que para hoje, temos algo além, <strong className="text-white">nunca visto no mercado.</strong>
          </p>
          <ArrowRight className="mx-auto mt-6 text-[#FF0055] animate-bounce" size={32} />
        </div>

        {/* The Offer Card */}
        <div className="glass p-8 md:p-12 border-2 border-[#FF0055]/50 rounded-3xl relative shadow-[0_0_50px_rgba(255,0,85,0.15)] bg-black/80 backdrop-blur-md">
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#FF0055] to-[#FF00A2] px-8 py-3 rounded-full text-white font-bold whitespace-nowrap shadow-lg uppercase text-sm md:text-base tracking-widest border border-white/20">
            Oferta Exclusiva para quem está aqui hoje
          </div>

          <div className="mt-8 mb-12 text-left max-w-xl mx-auto">
            <h4 className="font-heading font-bold text-2xl text-white mb-8 text-center bg-white/5 py-4 rounded-xl">Você vai receber:</h4>
            <ul className="space-y-5 text-lg text-gray-200">
              <li className="flex items-center gap-4"><CheckCircle className="text-[#00FF87] shrink-0" size={28} /> 1 site pessoal profissional completo</li>
              <li className="flex items-center gap-4"><CheckCircle className="text-[#00FF87] shrink-0" size={28} /> Hospedagem com backup gratuito por 4 meses</li>
              <li className="flex items-center gap-4 text-sm text-[#A1A1AA] ml-11 -mt-2">(Após esse período: apenas R$ 49,90/mês)</li>
              <li className="flex items-center gap-4"><CheckCircle className="text-[#00FF87] shrink-0" size={28} /> Consultoria para ativação de domínio próprio</li>
              <li className="flex items-center gap-4"><CheckCircle className="text-[#00FF87] shrink-0" size={28} /> Consultoria para criação de e-mail corporativo</li>
            </ul>
            <div className="mt-8 p-4 bg-gradient-to-r from-[#00E5FF]/20 to-transparent border-l-4 border-[#00E5FF] rounded-r-lg">
              <p className="text-white font-semibold text-lg">
                Não precisa fazer nada. Nós ativamos tudo para você.
              </p>
            </div>
          </div>

          {/* Pricing */}
          <div className="my-12">
            <p className="text-gray-400 uppercase tracking-widest text-sm mb-2 font-semibold">Tudo isso por apenas</p>
            <h2 className="font-heading font-black text-6xl md:text-8xl text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-400 drop-shadow-2xl">
              R$ 799<span className="text-4xl text-[#FF0055]">,00</span>
            </h2>
          </div>

          {/* Bonus */}
          <div className="bg-[#FF0055]/10 border border-[#FF0055]/30 rounded-2xl p-8 text-left max-w-xl mx-auto mb-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-[#FF0055] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase">Ação Rápida</div>
            <h4 className="font-heading font-bold text-2xl text-white flex items-center gap-3 mb-4">
              <Gift className="text-[#FF0055]" size={28} /> Mas espere…
            </h4>
            <p className="text-[#A1A1AA] font-medium mb-6 text-lg">Para quem fechar agora <strong className="text-[#00FF87]">via PIX</strong>, bônus exclusivo:</p>
            <ul className="space-y-4 text-white/90">
              <li className="flex items-start gap-3"><Star className="text-yellow-400 mt-1 shrink-0" size={20} /> Mentoria prática de como ativar o Meta Business</li>
              <li className="flex items-start gap-3"><Star className="text-yellow-400 mt-1 shrink-0" size={20} /> Estratégias para engajar e crescer no Instagram usando IA</li>
            </ul>
          </div>

          {/* Call to Actions & QR Code */}
          <div className="flex flex-col items-center gap-8 border-t border-white/10 pt-12">
            
            <button 
              onClick={handleCopyPix}
              className="group relative cursor-pointer"
              title="Clique para Copiar o Código PIX"
            >
              <div className="bg-white p-4 rounded-3xl shadow-[0_0_30px_rgba(255,255,255,0.2)] group-hover:scale-105 transition-transform">
                <img src={qrCodeUrl} alt="QR Code PIX R$ 799" width="220" height="220" />
              </div>
              <div className="absolute inset-0 bg-black/60 rounded-3xl opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-all">
                <CheckCircle className="text-[#00FF87] mb-2" size={32} />
                <span className="text-white font-bold text-sm uppercase">Copiar PIX</span>
              </div>
            </button>
            
            <button 
              onClick={handleCopyPix}
              className="text-[#A1A1AA] text-sm font-mono bg-black/60 px-6 py-4 rounded-xl border border-white/10 flex flex-col items-center w-full max-w-sm hover:border-[#00FF87]/50 hover:bg-black/80 transition-colors"
            >
              <span className="uppercase text-xs mb-2 opacity-70 tracking-widest">Pix Copia e Cola (Clique):</span>
              <span className="text-[#00FF87] text-xl font-bold tracking-wider underline decoration-dashed decoration-[#00FF87]/50 underline-offset-4">COPIAR CÓDIGO</span>
            </button>

            <div className="flex flex-col gap-4 w-full max-w-md mt-4">
              <a 
                href={`https://wa.me/${pixKeyClean.replace(/\D/g, '')}?text=Olá! Acabei de pagar os R$ 799 à vista no PIX, segue meu comprovante!`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-[#00E5FF] to-[#00FF87] text-black font-black text-xl py-5 px-8 rounded-xl hover:scale-[1.02] transition-transform shadow-[0_0_40px_rgba(0,255,135,0.3)] flex items-center justify-center gap-3 uppercase tracking-wider w-full"
              >
                JÁ PAGUEI (ENVIAR COMPROVANTE)
              </a>
              
              <div className="text-center mt-6">
                <a 
                  href="https://mpago.la/1Cs4aMa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-[#A1A1AA] border border-white/10 hover:bg-white/5 hover:text-white py-4 px-8 rounded-xl transition-all w-full text-lg font-medium"
                >
                  <CreditCard size={20} className="text-[#FF0055]" />
                  Parcelar em até 12x
                </a>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

// Main Home Component
const Home = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      <HeroSection />
      <CasesMarquee />
      <GiveawaySection />
      <OfferSection />
      <HubSection />
      <PublicDrawSection />
      <Footer />
    </div>
  );
};



// PIX Payload Generator
function generatePix(chave, nome, cidade, valor = null) {
  let gui = "0014br.gov.bcb.pix01" + String(chave.length).padStart(2, "0") + chave;
  let s = "00020101021126" + String(gui.length).padStart(2, "0") + gui;
  s += "520400005303986";
  if (valor) {
      s += "54" + String(valor.length).padStart(2, "0") + valor;
  }
  s += "5802BR";
  s += "59" + String(nome.length).padStart(2, "0") + nome;
  s += "60" + String(cidade.length).padStart(2, "0") + cidade;
  let txid = "0503***";
  s += "62" + String(txid.length).padStart(2, "0") + txid;
  s += "6304";
  
  let crc = 0xFFFF;
  for (let i = 0; i < s.length; i++) {
      crc ^= s.charCodeAt(i) << 8;
      for (let j = 0; j < 8; j++) {
          if ((crc & 0x8000) > 0) crc = (crc << 1) ^ 0x1021;
          else crc = crc << 1;
      }
  }
  let crcStr = (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, "0");
  return s + crcStr;
}

// Floating Contact Component
const FloatingContact = () => {
  const [showPix, setShowPix] = useState(false);
  const pixKeyDisplay = "27 9 8176-1251";
  const pixKeyClean = "+5527981761251";
  
  const pixString = generatePix(pixKeyClean, "Danilo Tomich", "Vitoria");
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(pixString)}`;

  const copyPix = () => {
    navigator.clipboard.writeText("27981761251");
    toast.success("Chave PIX copiada para a área de transferência!");
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 flex flex-col gap-4 z-50">
        <button 
          onClick={() => setShowPix(true)}
          className="w-14 h-14 bg-[#FF9900] rounded-full flex items-center justify-center text-white shadow-lg shadow-[#FF9900]/20 hover:scale-110 transition-transform"
          title="Dar uma gorjeta"
        >
          <Coffee size={24} />
        </button>
        <a 
          href="https://wa.me/5527981761251"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center text-white shadow-lg shadow-[#25D366]/20 hover:scale-110 transition-transform"
          title="Chamar no WhatsApp"
        >
          <MessageCircle size={28} />
        </a>
      </div>

      {showPix && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setShowPix(false)}>
          <div className="bg-[#0A0C10] border border-white/10 p-8 rounded-2xl max-w-sm w-full text-center relative pointer-events-auto" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setShowPix(false)}
              className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
            <div className="w-16 h-16 bg-[#FF9900]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Coffee className="text-[#FF9900]" size={32} />
            </div>
            <h3 className="font-heading font-bold text-xl text-white mb-2">Gostou da Mesa de Talentos?</h3>
            <p className="text-[#A1A1AA] text-sm mb-6">Apoie com um café e me ajude a continuar desenvolvendo soluções de alto nível.</p>
            
            <div className="bg-white p-4 rounded-xl inline-block mb-4 shadow-lg">
              <img src={qrCodeUrl} alt="QR Code PIX" width="180" height="180" className="mx-auto" />
            </div>
            
            <div className="bg-black/50 p-4 rounded-xl border border-white/5 mb-4">
              <p className="text-[#A1A1AA] text-xs mb-1 uppercase tracking-wider">Chave PIX (Telefone)</p>
              <p className="text-[#00E5FF] font-mono text-xl font-bold">{pixKeyDisplay}</p>
            </div>

            <button 
              onClick={copyPix}
              className="btn-secondary w-full flex items-center justify-center gap-2 py-3"
            >
              <span>Copiar Chave</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
      <FloatingContact />
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0A0C10',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      />
    </div>
  );
}

export default App;
